import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const storage = getStorage(app);

// Only connect to emulators if explicitly enabled
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectStorageEmulator(storage, 'localhost', 9199);
}

// ============================================================
// Analytics (Consent Mode v2, region-aware)
// ============================================================
// Non-EEA users are auto-granted. EEA/UK/Swiss users default to denied
// and must accept via the cookie banner before any events are collected.
// All analytics calls are guarded so a failure never breaks app flow.

const EEA_TIMEZONE_PREFIXES = [
  'Europe/',
  'Atlantic/Azores',
  'Atlantic/Canary',
  'Atlantic/Madeira',
  'Atlantic/Reykjavik',
];

export const isEEARegion = (): boolean => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return EEA_TIMEZONE_PREFIXES.some((p) => tz.startsWith(p));
  } catch {
    return true; // fail-safe: assume consent required
  }
};

export type AnalyticsConsent = 'granted' | 'denied';
const CONSENT_STORAGE_KEY = 'romulus_analytics_consent';

export const getStoredAnalyticsConsent = (): AnalyticsConsent | null => {
  try {
    const v = localStorage.getItem(CONSENT_STORAGE_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
};

type LogEventFn = (name: string, params?: Record<string, unknown>) => void;
let logEventFn: LogEventFn | null = null;
let analyticsInitPromise: Promise<void> | null = null;

// Analytics opt-out for the public /demo experience. Two layers:
//   (a) Direct visits to /demo never call getAnalytics() at all — no SDK,
//       no realtime active-user pings, no first_visit_manual.
//   (b) For visitors who navigate from / into /demo (SDK already alive),
//       DemoStateProvider calls setDemoAnalyticsBlocked(true) on mount,
//       which flips setAnalyticsCollectionEnabled and short-circuits any
//       further logEvent calls until they leave /demo.
let demoAnalyticsBlocked = false;
let analyticsInstanceRef: unknown = null;
const isOnDemoPath = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/demo');

export const setDemoAnalyticsBlocked = async (blocked: boolean): Promise<void> => {
  demoAnalyticsBlocked = blocked;
  if (!analyticsInstanceRef) return;
  try {
    const mod = await import('firebase/analytics');
    mod.setAnalyticsCollectionEnabled(
      analyticsInstanceRef as Parameters<typeof mod.setAnalyticsCollectionEnabled>[0],
      !blocked,
    );
  } catch (err) {
    console.warn('[analytics] toggle failed', err);
  }
};

const FIRST_VISIT_STORAGE_KEY = 'romulus_first_visit_fired';

// Fires `first_visit_manual` exactly once per device (localStorage-gated).
// Firebase's built-in first_visit is unreliable with Consent Mode + SPAs
// re-initializing, so we track it ourselves. Also fires a device-tagged
// variant (`first_visit_manual_mobile` | `first_visit_manual_desktop`) so
// GA4 funnels can segment by form factor without a custom dimension.
const detectDevice = (): 'mobile' | 'desktop' => {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent || (navigator as Navigator & { vendor?: string }).vendor || '';
  const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const smallScreen = window.innerWidth < 768;
  return uaMobile || smallScreen ? 'mobile' : 'desktop';
};

const fireFirstVisitOnce = (): void => {
  if (!logEventFn) return;
  try {
    if (localStorage.getItem(FIRST_VISIT_STORAGE_KEY) === '1') return;
    const device = detectDevice();
    const params = {
      landing_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
      referrer: typeof document !== 'undefined' ? document.referrer || '(none)' : undefined,
      device,
    };
    logEventFn('first_visit_manual', params);
    logEventFn(`first_visit_manual_${device}`, params);
    localStorage.setItem(FIRST_VISIT_STORAGE_KEY, '1');
  } catch {
    // localStorage unavailable - skip rather than spam events every load
  }
};

export const initAnalytics = (): Promise<void> => {
  if (analyticsInitPromise) return analyticsInitPromise;
  analyticsInitPromise = (async () => {
    try {
      if (import.meta.env.VITE_USE_EMULATORS === 'true' || import.meta.env.DEV) return;
      // Skip analytics entirely for direct visits to /demo. Keeps the demo
      // out of realtime active-user counts and the first_visit_manual funnel.
      if (isOnDemoPath()) return;
      const mod = await import('firebase/analytics');
      const supported = await mod.isSupported();
      if (!supported) return;

      const stored = getStoredAnalyticsConsent();
      const consent: AnalyticsConsent =
        stored ?? (isEEARegion() ? 'denied' : 'granted');

      mod.setConsent({
        analytics_storage: consent,
        ad_storage: consent,
        ad_user_data: consent,
        ad_personalization: consent,
      });

      const instance = mod.getAnalytics(app);
      analyticsInstanceRef = instance;
      logEventFn = (name, params) => {
        try {
          mod.logEvent(instance, name, params as Record<string, unknown>);
        } catch (err) {
          console.warn('[analytics] logEvent failed', err);
        }
      };
      if (consent === 'granted') fireFirstVisitOnce();
    } catch (err) {
      console.warn('[analytics] init failed', err);
    }
  })();
  return analyticsInitPromise;
};

export const setAnalyticsConsent = async (granted: boolean): Promise<void> => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, granted ? 'granted' : 'denied');
  } catch {
    // localStorage unavailable - consent won't persist across sessions
  }
  try {
    if (import.meta.env.VITE_USE_EMULATORS === 'true' || import.meta.env.DEV) return;
    const mod = await import('firebase/analytics');
    const supported = await mod.isSupported();
    if (!supported) return;
    const flag: AnalyticsConsent = granted ? 'granted' : 'denied';
    mod.setConsent({
      analytics_storage: flag,
      ad_storage: flag,
      ad_user_data: flag,
      ad_personalization: flag,
    });
    if (granted && !logEventFn) {
      if (isOnDemoPath()) return;
      const instance = mod.getAnalytics(app);
      analyticsInstanceRef = instance;
      logEventFn = (name, params) => {
        try {
          mod.logEvent(instance, name, params as Record<string, unknown>);
        } catch (err) {
          console.warn('[analytics] logEvent failed', err);
        }
      };
    }
    if (granted) fireFirstVisitOnce();
  } catch (err) {
    console.warn('[analytics] consent update failed', err);
  }
};

export const logAnalyticsEvent: LogEventFn = (name, params) => {
  try {
    if (demoAnalyticsBlocked || isOnDemoPath()) return;
    logEventFn?.(name, params);
  } catch {
    // never throw - analytics must not affect user-facing flow
  }
};

export type SupportTicketStatus = 'open' | 'answered' | 'closed';

export interface SupportTicketSummary {
  id: string;
  email: string | null;
  subject: string | null;
  message: string;
  status: SupportTicketStatus;
  pageAtSubmit: string | null;
  createdAt: number | null;
  answeredAt: number | null;
  userId: string | null;
  planId: string | null;
  subscriptionActive: boolean | null;
}

export const callFunction = <T, R>(name: string) => {
  const fn = httpsCallable<T, R>(functions, name);
  return async (data: T): Promise<R> => {
    const result = await fn(data);
    return result.data;
  };
};

export const cloudFunctions = {
  scrapeGooglePlaces: callFunction<
    { campaignId: string; query: string; location: string; maxLeads?: number; isLocalCampaign?: boolean },
    { sessionId: string; leadsCreated: number; leadsSkipped: number; leadsNoEmail: number; leadsWithoutWebsite: number; leadsWithWebsite: number; nicheSearched: string }
  >('scrapeGooglePlaces'),

  generateSiteContent: callFunction<
    { leadId: string; templateType: string; selectedFeatures?: string[]; selectedLayouts?: string[] },
    { siteId: string; content: Record<string, unknown> }
  >('generateSiteContent'),

  generatePreview: callFunction<
    { siteId: string },
    { success: boolean; previewHtml: string }
  >('generatePreview'),

  markSiteSold: callFunction<
    { siteId: string },
    { success: boolean }
  >('markSiteSold'),

  createCheckout: callFunction<
    { planId: 'starter' | 'cashflow' | 'scale'; successUrl?: string; cancelUrl?: string },
    { sessionUrl: string }
  >('createCheckout'),

  createPortalSession: callFunction<Record<string, never>, { url: string }>('createPortalSession'),

  syncSubscriptionStatus: callFunction<
    Record<string, never>,
    {
      synced: boolean;
      message: string;
      planId: string;
      subscriptionActive?: boolean;
      quotas?: { leadsLimit: number; sitesLimit: number };
    }
  >('syncSubscriptionStatus'),

  updateSiteContent: callFunction<
    {
      siteId: string;
      content: Record<string, unknown>;
      selectedFeatures?: string[];
      price?: number;
      templateType?: string;
      customTheme?: { primaryColor: string; accentColor: string; bgColor: string; textColor: string; headingFont?: string; bodyFont?: string };
      businessData?: { photos?: string[] };
      generatedHtml?: string;
    },
    { success: boolean }
  >('updateSiteContent'),

  getPreviewShareLink: callFunction<
    { siteId: string },
    { token: string }
  >('getPreviewShareLink'),

  getPreviewByToken: callFunction<
    { token: string },
    { name: string; templateType: string; previewHtml: string | null; status: string; sitePages?: string | null; siteMeta?: { siteType?: string; sectionsUsed?: string[] } | null }
  >('getPreviewByToken'),

  updateBuyerContactInfo: callFunction<
    {
      token: string;
      contactInfo: {
        phone?: string;
        email?: string;
        address?: string;
        city?: string;
        state?: string;
        zip?: string;
        hours?: string;
        socialFacebook?: string;
        socialInstagram?: string;
        socialTwitter?: string;
      };
    },
    { success: boolean }
  >('updateBuyerContactInfo'),

  getSiteCustomizationStatus: callFunction<
    { token: string },
    {
      name: string;
      status: string;
      customizationComplete: boolean;
      buyerContactInfo: Record<string, unknown> | null;
      previewHtml: string | null;
    }
  >('getSiteCustomizationStatus'),


  createSitePurchaseCheckout: callFunction<
    { token: string; customerEmail?: string },
    { sessionUrl: string }
  >('createSitePurchaseCheckout'),

  completeSitePurchase: callFunction<
    { sessionId: string; token: string },
    { success: boolean; alreadyProcessed?: boolean }
  >('completeSitePurchase'),

  downloadSiteZip: callFunction<
    { token: string },
    { fileName: string; zipBase64: string }
  >('downloadSiteZip'),

  getConnectOnboardingLink: callFunction<
    { country?: string },
    { url: string }
  >('getConnectOnboardingLink'),

  getConnectLoginLink: callFunction<
    Record<string, never>,
    { url: string }
  >('getConnectLoginLink'),

  getConnectStatus: callFunction<
    Record<string, never>,
    {
      hasAccount: boolean;
      isOnboarded: boolean;
      canReceivePayments: boolean;
      status?: string;
      requirements?: string[];
      /** Country the existing payout account is locked to. */
      country?: string | null;
      /** Country of the card the seller paid Romulus with, if known. */
      billingCountry?: string | null;
      /** True while the account is not live, so it can move to another country. */
      canChangeCountry?: boolean;
    }
  >('getConnectStatus'),

  getConnectCountries: callFunction<
    Record<string, never>,
    { countries: string[] }
  >('getConnectCountries'),

  suggestCities: callFunction<
    { input: string },
    { suggestions: Array<{ name: string; fullName: string }> }
  >('suggestCities'),

  reportLeadHasWebsite: callFunction<
    { leadId: string },
    { refunded: boolean; alreadyReported: boolean }
  >('reportLeadHasWebsite'),

  listSupportTickets: callFunction<
    Record<string, never>,
    { tickets: SupportTicketSummary[] }
  >('listSupportTickets'),

  updateSupportTicket: callFunction<
    { ticketId: string; status: SupportTicketStatus },
    { success: boolean }
  >('updateSupportTicket'),

  processPendingCommissions: callFunction<
    Record<string, never>,
    { processed: number; failed: number; total: number; results: Array<{ commissionId: string; status: string; transferId?: string; error?: string }> }
  >('processPendingCommissions'),

  updateSubscription: callFunction<
    { newPlanId: 'starter' | 'cashflow' | 'scale' },
    {
      success: boolean;
      type: 'upgrade' | 'downgrade';
      immediate: boolean;
      newPlanId: string;
      effectiveDate?: string;
    }
  >('updateSubscription'),

  cancelPendingPlanChange: callFunction<
    Record<string, never>,
    { success: boolean }
  >('cancelPendingPlanChange'),

  deleteAccount: callFunction<
    Record<string, never>,
    { success: boolean }
  >('deleteAccount'),

  romulusChat: callFunction<
    {
      siteId: string;
      message: string;
      currentContent: {
        headline: string;
        aboutText: string;
        services: Array<{ name: string; description: string; icon?: string; price?: string }>;
        testimonials: Array<{ name: string; role?: string; content: string; rating?: number }>;
        seo: { title: string; description: string };
        faq?: Array<{ question: string; answer: string }>;
        videoUrl?: string;
      };
      customTheme: { primaryColor: string; accentColor: string; bgColor?: string; textColor?: string };
      templateType: string;
      currentHtml?: string; // Full rendered HTML for visual context
    },
    {
      response: string;
      actions: string[];
      updatedContent?: Partial<{
        headline: string;
        aboutText: string;
        services: Array<{ name: string; description: string; icon?: string; price?: string }>;
        testimonials: Array<{ name: string; role?: string; content: string; rating?: number }>;
        seo: { title: string; description: string };
      }>;
      updatedTheme?: Partial<{ primaryColor: string; accentColor: string; bgColor: string; textColor: string }>;
      updatedHtml?: string; // Full modified HTML document returned by AI
    }
  >('romulusChat'),
};

export default app;
